import React, { useState, useEffect, useContext } from "react";
import styles from "./InitialMessage.module.css";
import iconInfo from "../../assets/info.svg";
import iconGuide from "../../assets/guide.svg";
import iconFAQ from "../../assets/faq.svg";
import iconLogo from "../../assets/logo.png";

// Import icons from react-icons
import { BsBriefcase } from 'react-icons/bs';
import { BsCheckCircle } from 'react-icons/bs';
import { BsLock } from 'react-icons/bs';
import { BsChatDots } from 'react-icons/bs';
import { BsExclamationTriangle } from 'react-icons/bs';
import { FiX, FiStar, FiInfo, FiAlertTriangle, FiTool } from 'react-icons/fi';
import { NotificationContext } from '../../context/NotificationContext';

const Modal = ({ show, handleClose, content }) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      // Some elements (e.g. SVG) return an object for className instead of a string, so use classList API
      if (target && target.classList && target.classList.contains(styles['modal'])) {
        handleClose();
      }
    };

    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [handleClose]);

  return (
    <div className={styles['modal']} style={{ display: show ? "flex" : "none" }}>
      <div className={styles['modal-content']}>
        <span className={styles['close']} onClick={handleClose}>
          &times;
        </span>
        {content}
      </div>
    </div>
  );
};

const Tile = ({ title, icon, onClick, className }) => (
  <div className={`${styles['tile']} ${className || ''}`} onClick={onClick}>
    <div className={styles['tile-header']}>
      {icon && <img src={icon} alt={title} className={styles['tile-icon']} />}
      <h3>{title}</h3>
    </div>
  </div>
);

// New Accordion component for FAQ items
const AccordionItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles['accordion-item']}>
      <div className={styles['accordion-question']} onClick={() => setIsOpen(!isOpen)}>
        <h3>{question}</h3>
        <div className={styles['icon-circle']}>
          {isOpen ? '-' : '+'}
        </div>
      </div>
      {isOpen && <div className={styles['accordion-answer']}>{answer}</div>}
    </div>
  );
};

// Simple guidance section header with React icon
const GuidanceHeader = ({ icon, title }) => (
  <div className={styles['guidance-header']}>
    <div className={styles['icon-circle']}>
      {icon}
    </div>
    <h2>{title}</h2>
  </div>
);

// Notification type to icon/class mapping
const NOTIFICATION_TYPE_MAP = {
  feature: { icon: <FiStar />, className: styles['notification-card-feature'] },
  info: { icon: <FiInfo />, className: styles['notification-card-info'] },
  warning: { icon: <FiAlertTriangle />, className: styles['notification-card-warning'] },
  maintenance: { icon: <FiTool />, className: styles['notification-card-maintenance'] },
};

const NotificationCard = ({ notification, onDismiss }) => {
  const typeConfig = NOTIFICATION_TYPE_MAP[notification.type] || NOTIFICATION_TYPE_MAP.info;

  return (
    <div className={`${styles['notification-card']} ${typeConfig.className}`}>
      <div className={styles['notification-icon']}>
        {typeConfig.icon}
      </div>
      <div className={styles['notification-body']}>
        <h4 className={styles['notification-title']}>{notification.title}</h4>
        <div
          className={styles['notification-message']}
          dangerouslySetInnerHTML={{ __html: notification.message }}
        />
      </div>
      <button
        className={styles['notification-dismiss']}
        onClick={() => onDismiss(notification.id)}
        aria-label="Dismiss notification"
      >
        <FiX />
      </button>
    </div>
  );
};

const InitialMessage = () => {
  const { notifications, dismissNotification } = useContext(NotificationContext);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [clickedTile, setClickedTile] = useState(null);

  const openModal = (tileTitle, content) => {
    setModalContent(content);
    setShowModal(true);
    setClickedTile(tileTitle);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
    setClickedTile(null);
  };

  // FAQ content organized as an array of question/answer objects
  const faqItems = [
    {
      question: "What are the different modes and how do I use them?",
      answer: (
        <>
          <p>COMPAS offers three distinct modes to suit different needs:</p>
          
          <h3>💬 Chat Mode</h3>
          <ul>
            <li><strong>Purpose:</strong> Fast responses from various LLMs for general questions and document/image analysis</li>
            <li><strong>Best for:</strong> General questions, document processing, code generation, quick answers</li>
            <li><strong>LLM Options:</strong>
              <ul>
                <li><strong>GPT-5.6 Sol:</strong> Deep reasoning for complex analysis and multi-step tasks (slower but most thorough)</li>
                <li><strong>Claude Opus 5.5:</strong> Anthropic's most advanced Opus model for long-running agents, coding, and professional work</li>
                <li><strong>Claude Fable 5:</strong> Creative storytelling and expressive content generation</li>
              </ul>
            </li>
            <li><strong>Note:</strong> Answers are limited to LLM's training data and could include inaccuracies or hallucinations</li>
          </ul>

          <h3>🤖 Agent Mode</h3>
          <ul>
            <li><strong>Purpose:</strong> Uses advanced reasoning models and specialized tools</li>
            <li><strong>Best for:</strong> Complex questions requiring internal knowledge, data analysis, or multi-step research</li>
            <li><strong>Features:</strong> Well-researched, comprehensive answers with higher accuracy and depth</li>
            <li><strong>Note:</strong> Slower but more thorough than Chat mode</li>
          </ul>

          <h3>📚 Doc Assistant Mode</h3>
          <ul>
            <li><strong>Purpose:</strong> Searches only official documentation sources to provide fact-based answers</li>
            <li><strong>Best for:</strong> Verified, accurate information from official documentation</li>
            <li><strong>Available Solutions:</strong> AEM, AEP, AJO, Analytics, Audience Manager, Campaign, Commerce, CJA, Knowledge Base Articles, Marketo, Adobe Pass, Target, Workfront</li>
            <li><strong>Features:</strong> Prevents AI hallucinations by sticking strictly to verified documentation with source links</li>
          </ul>

          <h3>How to Switch Modes:</h3>
          <ol>
            <li>Look for the mode dropdown button in the chat input area (left side)</li>
            <li>Click the current mode (shows icon and name like "Chat", "Agent", or "Doc Assistant")</li>
            <li>Select your desired mode from the dropdown menu</li>
            <li>For Doc Assistant mode, you'll also need to select which solution(s) to search</li>
          </ol>
        </>
      )
    },
    {
      question: "How do I use the Doc Assistant mode?",
      answer: (
        <>
          <ol>
            <li>
              <strong>Select Doc Assistant Mode</strong>
              <ul>
                <li>Click the mode dropdown in the chat input area</li>
                <li>Choose <strong>"Doc Assistant"</strong> from the list</li>
              </ul>
            </li>
            <li>
              <strong>Choose Your Solution(s)</strong>
              <ul>
                <li>A "Solutions" dropdown will appear next to the mode selector</li>
                <li>Click it to see available options</li>
                <li>Select one or multiple solutions you want to search</li>
              </ul>
            </li>
            <li>
              <strong>Ask a Specific Question</strong>
              <ul>
                <li>Enter a precise query related to your selected solution(s)</li>
                <li>Example: <em>"How do I create a project template in Workfront?"</em></li>
              </ul>
            </li>
            <li>
              <strong>Review the Response</strong>
              <ul>
                <li>COMPAS will provide answers based only on official documentation</li>
                <li>Responses include references to the source documentation</li>
              </ul>
            </li>
          </ol>
          <h3>Important Notes:</h3>
          <ul>
            <li><strong>Purpose:</strong> Designed for accurate, fact-based answers from official sources</li>
            <li><strong>Best Practice:</strong> Use for when you need verified information rather than general conversation</li>
            <li><strong>Tip:</strong> You must select at least one solution before you can send a message</li>
          </ul>
        </>
      )
    },
    {
      question: "Can I Upload Images or Documents?",
      answer: (
        <>
          <p><strong>Yes!</strong> Follow these steps:</p>
          <ol>
            <li>Click the <strong>upload icon +</strong> located to the left of the input box.</li>
            <li>Add the image or document you want to use.</li>
            <li>Ask your question based on the uploaded image or document.</li>
          </ol>
          <p><strong>Note:</strong> File upload works with all modes (Chat, Agent, and Doc Assistant).</p>
        </>
      )
    }
  ];

  return (
    <div className={styles['initial-message']}>
      <div className={styles['logo-container']}>
        <img src={iconLogo} alt='Logo' className={styles['logo']} />
      </div>
      <div className={styles['tiles-container']}>
        <Tile
          className={clickedTile === "Intro" ? styles['clicked'] : ""}
          title='Intro'
          icon={iconInfo}
          onClick={() => {
            openModal(
              "Intro",
              <>
                <section id='welcome'>
                  <header>
                      <h1>Welcome to COMPAS</h1>
                  </header>
                  <section>
                      <p><strong>COMPAS</strong> (Comprehensive Model for Professional Assistance and Support) is your trusted AI-powered assistant designed to empower Customer Experience and Success teams across organizations.</p>
                  </section>
                  <section>
                      <p>Whether you're a <span className={styles['role']}><strong>Field Engineer</strong></span> troubleshooting complex systems, a <span className={styles['role']}><strong>Technical Account Manager</strong></span> optimizing client environments, a <span className={styles['role']}><strong>Customer Success Manager</strong></span> driving impactful outcomes, a <span className={styles['role']}><strong>Support Engineer</strong></span> resolving critical issues, or a <span className={styles['role']}><strong>People Manager</strong></span> ensuring team efficiency—COMPAS is here to support you.</p>
                  </section>
                  <section>
                      <p>Built to address the unique challenges faced by professionals in customer-facing roles, COMPAS combines advanced <strong>GenAI capabilities</strong> with contextual intelligence to help you deliver exceptional results. From streamlining workflows to enhancing collaboration, COMPAS is your partner in achieving operational excellence and fostering customer satisfaction.</p>
                  </section>
                  <section>
                      <p><strong>Customer Experience and Success teams</strong> are the backbone of any organization, ensuring clients receive the value they expect while driving long-term relationships. COMPAS is designed to enhance your ability to meet these goals by simplifying complex tasks, improving efficiency, and enabling you to focus on what matters most—delivering exceptional customer experiences.</p>
                  </section>
                  <section>
                      <p>With COMPAS, you gain a reliable assistant that adapts to your unique needs, whether you're solving technical challenges, managing client relationships, or leading teams. By leveraging cutting-edge AI technology, COMPAS empowers you to navigate complexities, drive innovation, and achieve success in every interaction.</p>
                  </section>
                  <section>
                      <p>At COMPAS, we understand the importance of collaboration and precision in customer-facing roles. Our mission is to help you unlock your full potential, enabling you to deliver impactful solutions and build lasting connections with our customers.</p>
                  </section>
                  <footer>
                      <p>Thank you for choosing COMPAS as your partner in excellence. Let's work together to redefine the future of Customer Experience and Success.</p>
                  </footer>
                </section>
              </>
            );
          }}
        />
        <Tile
          title='Guidance'
          className={clickedTile === "Guidance" ? styles['clicked'] : ""}
          icon={iconGuide}
          onClick={() => {
            openModal(
              "Guidance",
              <>
                <section id='guidance'>
                  <header>
                    <h1>Guidelines for Using COMPAS</h1>
                  </header>
                  <section>
                    <p>To maximize the value of COMPAS and ensure responsible usage, we've outlined key practices and considerations to help you and your team leverage this tool effectively.</p>
                  </section>
                  
                  <div className={styles['guidance-section']}>
                    <GuidanceHeader 
                      icon={<BsBriefcase />} 
                      title="Focus on Work-Related Tasks" 
                    />
                    <p>COMPAS is designed to support professional tasks within your role. Avoid using it for personal or non-work-related activities to maintain alignment with its intended purpose.</p>
                  </div>
                  
                  <div className={styles['guidance-section']}>
                    <GuidanceHeader 
                      icon={<BsCheckCircle />} 
                      title="Verify Before Sharing" 
                    />
                    <p>While COMPAS strives to deliver accurate and reliable information, it's essential to verify outputs before sharing them with customers. Ensure that solutions and recommendations are tailored to the specific customer scenario to uphold trust, professionalism, and accuracy.</p>
                  </div>
                  
                  <div className={styles['guidance-section']}>
                    <GuidanceHeader 
                      icon={<BsLock />} 
                      title="Prioritize Confidentiality" 
                    />
                    <p>Protecting sensitive information is critical. Avoid sharing confidential data with COMPAS, and always adhere to our organization's data protection policies when using the tool.</p>
                  </div>
                  
                  <div className={styles['guidance-section']}>
                    <GuidanceHeader 
                      icon={<BsChatDots />} 
                      title="Contribute to Continuous Improvement" 
                    />
                    <p>COMPAS evolves through feedback. If you encounter inaccuracies, gaps, or areas for improvement, share your insights through the designated feedback channels. Your input helps refine COMPAS and enhances its effectiveness for everyone.</p>
                    <p>Feedback Channel: <a href="https://adobe.enterprise.slack.com/archives/C073F1NC5RT" target="_blank" rel="noreferrer">COMPAS Feedback Slack Channel</a></p>
                  </div>
                  
                  <div className={styles['guidance-section']}>
                    <GuidanceHeader 
                      icon={<BsExclamationTriangle />} 
                      title="Understand Limitations" 
                    />
                    <p>While COMPAS is a powerful tool, it is not infallible. Certain situations may require human judgment, expertise, or nuanced decision-making. Use COMPAS as a complement to your skills, not a replacement.</p>
                  </div>
                </section>
              </>
            );
          }}
        />
        {/* <Tile
          className={clickedTile === "Examples" ? styles['clicked'] : ""}
          title='Examples'
          icon={iconExamples}
          onClick={() => {
            openModal(
              "Examples",
              <>
                <section id='examples'>
        
                  <h2>Solution Troubleshooting</h2>
                  <p>
                    <strong>Situation:</strong> An engineer needs to troubleshoot an issue with an Adobe Experience Cloud Solution.
                    <br />
                    <strong>COMPAS Role:</strong> Provides step-by-step troubleshooting guides and advice.
                    <br />
                    <strong>Example Prompt:</strong> "Troubleshooting and best practices for Report Builder in Adobe Analytics"
                  </p>
                  <hr />
        
                  <h2>Environment Review</h2>
                  <p>
                    <strong>Situation:</strong> An engineer needs to conduct a review of a customer's technical environment.
                    <br />
                    <strong>COMPAS Role:</strong> Processes data to highlight insights and areas of concern.
                    <br />
                    <strong>Example Prompt:</strong> "Analyze the provided data for the technical environment and suggest performance improvements."
                  </p>
                  <hr />
        
                  <h2>Launch Advisory</h2>
                  <p>
                    <strong>Situation:</strong> An engineer needs to generate a Launch Advisory recommendation document.
                    <br />
                    <strong>COMPAS Role:</strong> Support with the creation of comprehensive recommendations.
                    <br />
                    <strong>Example Prompt:</strong> "Generate a launch advisory recommendation based on the project's milestones documents provided."
                  </p>
                  <hr />
        
                  <h2>Deskside Coaching Preparation</h2>
                  <p>
                    <strong>Situation:</strong> An engineer is preparing for a enablement session for a customer.
                    <br />
                    <strong>COMPAS Role:</strong> Generates a customized session plan.
                    <br />
                    <strong>Example Prompt:</strong> "Create a enablement session plan for Adobe Marketo Engage Email Designer."
                  </p>
                  <hr />
        
                  <h2>Upgrade or Migration Assessment</h2>
                  <p>
                    <strong>Situation:</strong> An engineer reviews plans for an environment upgrade or migration.
                    <br />
                    <strong>COMPAS Role:</strong> Proposes recommendations for a successful upgrade or migration.
                    <br />
                    <strong>Example Prompt:</strong> "Analyze the provided data and assess the readiness for migrating from Magento 2.3 to Magento 2.4."
                  </p>
                  <hr />
        
                  <h2>Mentor Session Setup</h2>
                  <p>
                    <strong>Situation:</strong> An engineer needs to conduct a one-hour technical mentor session.
                    <br />
                    <strong>COMPAS Role:</strong> Provides a concise, ready-to-use presentation and talking points.
                    <br />
                    <strong>Example Prompt:</strong> "Prepare a mentor session outline on Adobe Experience Manager's latest features."
                  </p>
                </section>
              </>
            );
          }}
        /> */}
        <Tile
          className={clickedTile === "FAQ" ? styles['clicked'] : ""}
          title='FAQ'
          icon={iconFAQ}
          onClick={() => {
            openModal(
              "FAQ",
              <>
                <section id="faq">
                  <header>
                    <h1>Frequently Asked Questions</h1>
                  </header>
                  <div className={styles['accordion-container']}>
                    {faqItems.map((item, index) => (
                      <AccordionItem 
                        key={index}
                        question={item.question}
                        answer={item.answer}
                      />
                    ))}
                  </div>
                </section>
              </>
            );
          }}
        />
      </div>
      {notifications.length > 0 && (
        <div className={styles['whats-new-section']}>
          <h3 className={styles['whats-new-title']}>What's New</h3>
          <div className={styles['whats-new-list']}>
            {notifications.map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                onDismiss={dismissNotification}
              />
            ))}
          </div>
        </div>
      )}
      <Modal show={showModal} handleClose={closeModal} content={modalContent} />
    </div>
  );
};

export default InitialMessage;
